import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "../utils/toast";
import { bursariesApi } from "../api/hliLists";
import { errorMessageOf } from "../api/scholarships";
import { Card, Container, EmptyState, Loading, PageHeader, Select, Table } from "./ui";
import LoanStatusRow from "./selection/LoanStatusRow";
import { selectionColumns } from "./selection/settingsColumns";
import { BURSARIES_KEY, useYearState } from "./bursaries/shared";

const WINDOW_KEY = [BURSARIES_KEY, "window"];

// The HLI admin opens or closes their institution's bursary requests and follows the selections HEC sets for it
export default function HliSelectionComponent() {
  const queryClient = useQueryClient();
  const [year, setYear, years] = useYearState();

  const requestWindow = useQuery({
    queryKey: WINDOW_KEY,
    queryFn: async () => (await bursariesApi.window()).data,
  });

  const selections = useQuery({
    queryKey: [BURSARIES_KEY, "selections", "mine", year],
    enabled: Boolean(year),
    queryFn: async () => {
      try {
        return (await bursariesApi.mySelections(year)).data;
      } catch (error) {
        console.error("Failed to fetch the institution's selections:", error);
        toast.error(errorMessageOf(error, "Failed to fetch the selections."), { id: "hli-selections-error" });
        throw error;
      }
    },
  });

  const setWindow = useMutation({
    mutationFn: (open) => bursariesApi.setWindow(open),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(WINDOW_KEY, data);
      toast.success(`Bursary requests ${data.open ? "opened" : "closed"}.`, { autoClose: 3000 });
    },
    onError: (error) => {
      console.error("Failed to change the request window:", error);
      toast.error(errorMessageOf(error, "Failed to change the request window."));
    },
  });

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Selection & Loan Management"
        subtitle="Manage when your students may ask for a bursary, and follow the selections HEC sets for your institution"
        actions={<Select aria-label="Academic year" value={year} onChange={(e) => setYear(e.target.value)} options={years} className="w-44" />}
      />

      <Card title="Bursary Request Settings" subtitle="Open or close bursary requests for your institution" bodyClassName="p-0" className="max-w-2xl">
        {requestWindow.isPending ? (
          <Loading label="Loading settings…" />
        ) : requestWindow.isError ? (
          <EmptyState title="Failed to load settings" description={errorMessageOf(requestWindow.error, "The request status could not be loaded.")} onRefresh={requestWindow.refetch} />
        ) : (
          <ul>
            <LoanStatusRow name="My institution" open={requestWindow.data.open} loading={setWindow.isPending} onChange={(open) => setWindow.mutate(open)} />
          </ul>
        )}
      </Card>

      <Card title="Selections" subtitle="How many of your students HEC funds per study area; grants reach students once HEC approves">
        <Table cardView
          columns={selectionColumns(false)}
          data={selections.data ?? []}
          isLoading={selections.isPending && Boolean(year)}
          searchable={false}
          exportName="my-selections"
          emptyTitle="HEC has set no selection for your institution this year"
        />
      </Card>
    </Container>
  );
}
